import React, { useState } from "react";
import InputField from "../../CommonComponents/InputField";
import DragAndDropUpload from "../../CommonComponents/DragAndDropUpload";
import Textarea from "../../CommonComponents/Textarea";
import IconButton from "../../CommonComponents/IconButton";
import Button from "../../Button";
import Toast from "../../CommonComponents/Toast";
import useAuth from "../../../Hooks/useAuth";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import { RESULT_STATUS } from "../../../constants";

const supportedFormat = ".jpeg, .jpg, .png",
  supportMimeTypes = ["image/png", "image/jpg", "image/apng", "image/jpeg"];

const validators = {
  name: (v) =>
    !v || v?.trim() === ""
      ? "Name is required"
      : v.length > 100
      ? "Max characters: 100"
      : "",
  description: (v) => (v?.length > 300 ? "Max characters: 300" : ""),
  thumbnail: (v) =>
    v && !supportMimeTypes.includes(v?.type)
      ? `Unsupported file type: ${v?.type}. Allowed formats are: ${supportedFormat}`
      : "",
};

const CreatePath = ({ setOpenModal, pathDetail = {}, setPathDetail }) => {
  const { isLoaded, setIsLoaded, navigate, setShowNotify } = useAuth();
  const axios = useAxiosPrivate();

  const isEdit = Number.isInteger(+pathDetail.id);

  const [thumbnail, setThumbnail] = useState(null);
  const [errors, setErrors] = useState({});
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const onChangeDetails = (field, value) => {
    setPathDetail((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: validators[field](value),
    }));
  };

  const onDragOver = (event) => event.preventDefault();

  const onUploadFile = (event, files = []) => {
    if (files.length > 1) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: "Multiple files are not allowed to upload",
      }));
      event.target.value = "";
      return;
    }
    const isUploadableFormat = files.every((file) =>
      supportMimeTypes.includes(file.type),
    );
    if (!isUploadableFormat) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: `Unsupported file type: ${
          files[0].type.split("/")[1]
        }. Allowed formats are: ${supportedFormat}`,
      }));
      event.target.value = "";
      return;
    }
    let reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result.length > 150000) {
        setErrors((prev) => ({
          ...prev,
          thumbnail: "File size is too large.",
        }));
        event.target.value = "";
        return;
      }

      setPathDetail((prev) => ({
        ...prev,
        thumbnail: reader.result,
      }));
    };
    reader.readAsDataURL(files[0]);
    setThumbnail(files[0]);
    setErrors((prev) => ({
      ...prev,
      thumbnail: "",
    }));
  };

  const onDrop = (event) => {
    const files = Array.from(event.dataTransfer.files);
    onUploadFile(event, files);
  };

  const onUploadThumbnail = (event) => {
    console.log("event", event.target.files);
    const files = Array.from(event.target.files);
    onUploadFile(event, files);
  };
  const onRemoveThumbnail = () => {
    setPathDetail((prev) => ({ ...prev, thumbnail: null }));
    setThumbnail(null);
  };

  const onSaveOrUpdate = async () => {
    console.log("thumbnail", thumbnail);
    console.log("pathdetails", pathDetail);
    const errors = {};
    Object.entries(pathDetail).forEach(([field, value]) => {
      if (validators[field]) {
        const error =
          field === "thumbnail"
            ? validators[field](thumbnail)
            : validators[field](value);
        if (error) {
          errors[field] = error;
        }
      }
    });
    setErrors(errors);

    if (Object.keys(errors).length) {
      // toast message for fix the highlighted error
      setToastOpen(true);
      setToastMessage("Please fix the highlighted errors before save.");
      return;
    }

    //save api
    try {
      setIsLoaded(true);
      if (isEdit) {
        const { data } = await axios.put(
          `node/admin/learningpath/${pathDetail.id}`,
          pathDetail,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          setOpenModal(false);
        } else {
          setToastOpen(true);
          setToastMessage(
            data?.msg ?? `Unable to update the path. Please try again later`,
          );
          return;
        }
      } else {
        const { data } = await axios.post(
          "node/admin/learningpath",
          pathDetail,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        console.log("data", data);
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          setOpenModal(false);
          navigate(`learningpath/${data.lpId}`);
        } else {
          setToastOpen(true);
          setToastMessage(
            data?.msg ?? `Unable to create the path. Please try again later`,
          );
          return;
        }
      }
    } catch (error) {
      console.error("error in create path", error);
      let isUnAuth = error.message.includes("403");
      if (isUnAuth) {
        setShowNotify({
          show: true,
          msg: "You have been logged-out due to inactivity. Login again.",
          title: "Warning !",
          isUnAuth,
        });
        setOpenModal(false);
        return;
      }
      setToastOpen(true);
      setToastMessage(
        error.message ?? `Unable to create the path. Please try again later`,
      );
    } finally {
      setIsLoaded(false);
    }
  };

  const buttonText = `${
    isEdit
      ? `${isLoaded ? "Updating" : "Update"}`
      : `${isLoaded ? "Saving" : "Save"}`
  }`;

  return (
    <div className="admin-path-create-container">
      <InputField
        label="Name"
        name="name"
        placeholder="Enter the path name"
        value={pathDetail.name}
        onChange={(event) =>
          onChangeDetails(event.target.name, event.target.value)
        }
        hintText={errors.name}
        destructive={!!errors.name}
      />
      <Textarea
        label="Description"
        placeholder="Description"
        name="description"
        value={pathDetail.description}
        onChange={(event) =>
          onChangeDetails(event.target.name, event.target.value)
        }
        hintText={errors.description}
        destructive={!!errors.description}
      />
      <DragAndDropUpload
        label="Thumbnail"
        text="Drop your attachement here"
        supportFormat={supportedFormat}
        className="admin-path-create-upload"
        onUpload={onUploadThumbnail}
        file={thumbnail}
        onDragOver={onDragOver}
        onRemoveFile={onRemoveThumbnail}
        onDrop={onDrop}
        hintText={errors.thumbnail}
        destructive={!!errors.thumbnail}
        button={
          <div className="admin-path-create-upload-icon-wrapper">
            <IconButton
              size="sm"
              hierarchy="secondaryGrey"
              className="admin-path-create-upload-icon"
              children={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 13V14.2C19 15.8802 19 16.7202 18.673 17.362C18.3854 17.9265 17.9265 18.3854 17.362 18.673C16.7202 19 15.8802 19 14.2 19H5.8C4.11984 19 3.27976 19 2.63803 18.673C2.07354 18.3854 1.6146 17.9265 1.32698 17.362C1 16.7202 1 15.8802 1 14.2V13M15 6L10 1M10 1L5 6M10 1V13"
                    stroke="#344054"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
            <p className="admin-path-create-upload-text">Upload file</p>
          </div>
        }
      />
      <div className="admin-path-create-btn-container">
        <Button
          size="sm"
          hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
          onClick={() => setOpenModal(false)}
        />
        <Button
          size="sm"
          hierarchy={{
            type: "primary",
            buttonText: buttonText,
          }}
          disable={isLoaded}
          onClick={onSaveOrUpdate}
        />
      </div>
      <Toast
        open={toastOpen}
        type="warning"
        message={toastMessage}
        onClose={() => setToastOpen(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </div>
  );
};

export default CreatePath;
